import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
  Form,
  FormFeedback,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import * as Yup from "yup";
import { toast } from "react-toastify";

import { CreateSubjectRequest, ParsedError } from "../../interfaces";
import {
  useGetSubjectQuery,
  useAddSubjectMutation,
  useUpdateSubjectMutation,
} from "../../store/api/subjectApi";
import { ActivityButton, SequenceNumberInput } from "../../components/Common";
import { ErrorHandlingTypeEnum, RoleEnum, SequenceEnum } from "../../enums";
import { FeaturesSelect } from "../../components/Selects/FeaturesSelect";
import { SelectListDto } from "../../models";
import { AccountState } from "../../store/slices/accountSlice";

type TProps = {
  id?: string | null;
  show: boolean;
  onCloseClick: (event: any) => void;
  courseId: string;
  gradeId: string;
  /** Sequence for the current grade (preferred over findOne when editing). */
  initialSequenceNumber?: number | null;
};
export const AddEditSubjectModal: React.FC<TProps> = ({
  id,
  show,
  onCloseClick,
  courseId,
  gradeId,
  initialSequenceNumber,
}) => {
  const { t, i18n } = useTranslation();
  const [selectedFeatures, setSelectedFeatures] = useState<any[]>([]);
  const isEdit = !!id;
  const profile = useSelector(
    (state: { account: AccountState }) => state.account.userProfile,
  );
  const isSuperAdmin = profile?.role?.key === RoleEnum.SUPER_ADMIN;

  const getEmptyValues = (): CreateSubjectRequest => ({
    name: { en: "", ar: "" },
    courseId,
    gradeId,
    isFree: false,
    sequenceNumber: 0,
    features: [],
  });

  const {
    data: subject,
    error: errorSubject,
    isLoading: isLoadingSubject,
  } = useGetSubjectQuery(
    { id: id as string, gradeId },
    { skip: !id || !show },
  );

  const [
    addSubject,
    { isLoading: isLoadingAdd, error: errorAdd, isError: isErrorAdd },
  ] = useAddSubjectMutation();

  const [
    updateSubject,
    { isLoading: isLoadingUpdate, error: errorUpdate, isError: isErrorUpdate },
  ] = useUpdateSubjectMutation();

  //#region form
  const form = useFormik<CreateSubjectRequest>({
    enableReinitialize: false,
    initialValues: getEmptyValues(),
    validationSchema: Yup.object({
      name: Yup.object({
        en: Yup.string()
          .max(
            100,
            t("maxLength", { maxLength: 100, displayName: "englishValue" }),
          )
          .required(
            t("isNotEmpty", {
              displayName: "englishValue",
            }),
          ),
        ar: Yup.string()
          .max(
            100,
            t("maxLength", { maxLength: 100, displayName: "arabicValue" }),
          )
          .required(
            t("isNotEmpty", {
              displayName: "arabicValue",
            }),
          ),
      }),
      courseId: Yup.string(),
      gradeId: Yup.string(),
      isFree: Yup.bool(),
      sequenceNumber: Yup.number().required(
        t("isNotEmpty", {
          displayName: "sequenceNumber",
        }),
      ),
      features: Yup.array().of(Yup.string()),
    }),
    onSubmit: async (values) => {
      try {
        if (id) {
          await updateSubject({
            id,
            data: {
              ...values,
              courseId,
              gradeId,
              sequenceNumber: Number(values.sequenceNumber),
              features: selectedFeatures?.map((sf) => sf.value),
            },
          }).unwrap();
        } else {
          await addSubject({
            ...values,
            courseId,
            gradeId,
            sequenceNumber: Number(values.sequenceNumber),
            features: selectedFeatures?.map((sf) => sf.value),
          }).unwrap();
        }
        toast.success(t("savedSuccessfully"), { autoClose: 3000 });
        onClose();
      } catch (err) {}
    },
  });
  //#endregion

  //#region handlers
  const onClose = () => {
    form.resetForm({ values: getEmptyValues() });
    setSelectedFeatures([]);
    onCloseClick(!show);
  };

  const onChangeFeatures = (changedValue: any) => {
    setSelectedFeatures(changedValue || []);
  };

  //#endregion

  //#region effects
  useEffect(() => {
    if (!show) {
      form.resetForm({ values: getEmptyValues() });
      setSelectedFeatures([]);
      return;
    }

    if (id) {
      if (!subject) return;
      form.setValues({
        name: subject.name,
        courseId,
        gradeId,
        isFree: subject.isFree,
        isInternal: subject.isInternal === true,
        sequenceNumber:
          initialSequenceNumber ?? subject.sequenceNumber ?? 0,
        features: [],
      });
      setSelectedFeatures(
        (subject.features || []).map((item: SelectListDto) => ({
          value: item.id,
          label: i18n.language === "ar" ? item.value.ar : item.value.en,
        })),
      );
      return;
    }

    form.resetForm({ values: getEmptyValues() });
    setSelectedFeatures([]);
  }, [
    show,
    id,
    subject,
    isLoadingSubject,
    courseId,
    gradeId,
    initialSequenceNumber,
  ]);

  useEffect(() => {
    let _error: ParsedError | null = null;
    if (!isLoadingAdd && isErrorAdd) _error = errorAdd as ParsedError;
    if (!isLoadingUpdate && isErrorUpdate) _error = errorUpdate as ParsedError;
    if (_error?.type == ErrorHandlingTypeEnum.FORM) {
      form.setErrors(_error.error);
    }
  }, [isLoadingAdd, isLoadingUpdate]);

  //#endregion

  return (
    <React.Fragment>
      <Modal
        id="addEdit"
        isOpen={show}
        toggle={() => {
          onClose();
        }}
        centered
      >
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
            return false;
          }}
          action="#"
        >
          <ModalHeader
            className="p-3"
            toggle={() => {
              onClose();
            }}
          >
            {id
              ? t("editItem", { item: "subject" })
              : t("addItem", { item: "subject" })}
          </ModalHeader>
          <ModalBody>
            {show ? (
              <SequenceNumberInput
                key={
                  isEdit
                    ? `edit-${id}-${gradeId}`
                    : `add-${courseId}-${gradeId}-${show}`
                }
                tabIndex={1}
                value={form.values.sequenceNumber}
                onChange={(e) => {
                  e.preventDefault();
                  const { value } = e.target;
                  const regex =
                    /^(0*[0-9][0-9]*(\.[0-9]*)?|0*\.[0-9]*[1-9][0-9]*)$/;
                  if (regex.test(value.toString())) {
                    form.setFieldValue("sequenceNumber", value);
                  } else {
                    form.setFieldValue("sequenceNumber", "");
                  }
                }}
                onBlur={form.handleBlur}
                invalid={
                  form.touched.sequenceNumber && form.errors.sequenceNumber
                    ? true
                    : false
                }
                sequenceType={SequenceEnum.SUBJECT}
                onGenerateSequenceNumber={(v: number) => {
                  form.setFieldValue("sequenceNumber", v);
                }}
                mode={isEdit ? "edit" : "add"}
                request={{
                  type: SequenceEnum.SUBJECT,
                  courseId,
                  gradeId,
                }}
              />
            ) : null}
            {form.touched.sequenceNumber && form.errors.sequenceNumber ? (
              <FormFeedback type="invalid">
                {form.errors.sequenceNumber as string}
              </FormFeedback>
            ) : null}
            <div className="mb-3">
              <label htmlFor="name.en" className="form-label">
                {t("nameEn")}
                <span className="text-danger">*</span>
              </label>
              <Input
                disabled={!!errorSubject}
                autoComplete="off"
                id="name.en"
                name="name.en"
                value={form.values?.name.en}
                type="text"
                className="form-control"
                placeholder={t("enterInEnglish")}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                invalid={
                  form.touched.name?.en && form.errors.name?.en ? true : false
                }
              />
              {form.touched.name?.en && form.errors.name?.en ? (
                <FormFeedback type="invalid">
                  {form.errors.name?.en as string}
                </FormFeedback>
              ) : null}
            </div>
            <div className="mb-3">
              <label htmlFor="name.ar" className="form-label">
                {t("nameAr")}
                <span className="text-danger">*</span>
              </label>
              <Input
                style={{ fontFamily: "aljannat", direction: "rtl" }}
                disabled={!!errorSubject}
                autoComplete="off"
                id="name.ar"
                name="name.ar"
                value={form.values.name?.ar}
                type="text"
                className="form-control"
                placeholder={t("enterInArabic")}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                invalid={
                  form.touched.name?.ar && form.errors.name?.ar ? true : false
                }
              />
              {form.touched.name?.ar && form.errors.name?.ar ? (
                <FormFeedback type="invalid">
                  {form.errors.name?.ar as string}
                </FormFeedback>
              ) : null}
            </div>
            <div className="mb-3">
              <label className="form-label">{t("features")}</label>
              <FeaturesSelect
                isMulti={true}
                closeMenuOnSelect={false}
                value={selectedFeatures}
                onChange={onChangeFeatures}
                placeholder="selectFeatures"
              />
            </div>
            {isSuperAdmin ? (
              <div className="mb-3 form-check">
                <Input
                  type="checkbox"
                  className="form-check-input"
                  id="isInternal"
                  checked={form.values.isInternal === true}
                  onChange={(event) =>
                    form.setFieldValue("isInternal", event.target.checked)
                  }
                />
                <label className="form-check-label" htmlFor="isInternal">
                  {t("noProductionSubject")}
                </label>
                <div className="text-muted small mt-1">
                  {form.values.isInternal === true ? (
                    <span>{t("noProductionSubjectHint")} </span>
                  ) : null}
                  <Link to="/users/testers">{t("testers")}</Link>
                </div>
              </div>
            ) : null}
          </ModalBody>
          <ModalFooter>
            <Link
              to={"#"}
              onClick={onClose}
              className="btn btn-link link-success fw-medium"
            >
              <i className="ri-close-line me-1 align-middle"></i> {t("close")}
            </Link>
            <ActivityButton
              title="save"
              inProgressTitle="saving"
              color="success"
              className="btn btn-success"
              type="submit"
              inProgress={isLoadingAdd || isLoadingUpdate}
              disabled={isLoadingAdd || isLoadingUpdate || !!errorSubject}
            ></ActivityButton>
          </ModalFooter>
        </Form>
      </Modal>
    </React.Fragment>
  );
};
